/* eslint-disable */
/* ProcessPlantA6View — Process Plant Stage A.6.1
 * ===============================================
 *
 * Single page with four tabs:
 *   • Inputs & Run   — form for A.6.1 user knobs + Run button +
 *                      live summary stats (equipment count, plot
 *                      area, OISD-118 compliance)
 *   • 2D Plot Plan   — A1-sheet SVG with title block, zones, roads,
 *                      pipe racks, equipment, north arrow, legend.
 *                      Pan/zoom + download.
 *   • 3D Plant       — Babylon scene with equipment as box/cylinder
 *                      primitives, zones as colored ground patches,
 *                      pipe racks as elevated bridges, roads,
 *                      orbital camera + free-cam walkthrough mode.
 *   • 3D + Real World — Cesium photoreal tiles base, plant geometry
 *                      overlaid on top using lat/lon from A.1
 *                      site_coordinates. Same walkthrough toggle.
 *
 * Reads design basis A.1-A.5 from /api/projects/{pid}/process-plant/
 * a6/inputs-schema and submits via /api/projects/{pid}/process-plant/
 * a6/run.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import * as BABYLON from "@babylonjs/core";
import "@babylonjs/loaders";
import { Download, Play, Loader2, AlertTriangle, CheckCircle2,
          Box, Map as MapIcon, Settings, Globe, Sparkles,
          Maximize2, Minimize2 } from "lucide-react";
import api from "../../lib/api";
import ApsDrawingViewer from "../diagram/ApsDrawingViewer";
import { createCesiumIonTiles } from "../../utils/cesiumTiles";


const TABS = [
  { id: "inputs",   label: "Inputs & Run",  icon: Settings },
  { id: "plot2d",   label: "2D Plot Plan",  icon: MapIcon },
  { id: "plant3d",  label: "3D Plant",      icon: Box },
  { id: "real3d",   label: "3D + Real World", icon: Globe },
];


// ────────────────────────────────────────────────────────────────────
//  Reusable fullscreen toggle for any container ref. Uses the standard
//  Fullscreen API so the requested element fills the whole viewport
//  (covering the app chrome). Listens to fullscreenchange to swap the
//  icon between Maximize / Minimize.
// ────────────────────────────────────────────────────────────────────
function FullscreenButton({ targetRef, testId }) {
  const [isFs, setIsFs] = useState(false);
  useEffect(() => {
    const onChange = () => {
      const fsEl = document.fullscreenElement
                    || document.webkitFullscreenElement;
      setIsFs(!!(targetRef.current && fsEl === targetRef.current));
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, [targetRef]);

  const toggle = () => {
    const el = targetRef.current;
    if (!el) return;
    const fsEl = document.fullscreenElement
                  || document.webkitFullscreenElement;
    if (!fsEl) {
      (el.requestFullscreen || el.webkitRequestFullscreen).call(el);
    } else {
      (document.exitFullscreen
        || document.webkitExitFullscreen).call(document);
    }
  };

  const Icon = isFs ? Minimize2 : Maximize2;
  return (
    <button onClick={toggle}
            data-testid={testId}
            title={isFs ? "Exit full screen" : "Full screen"}
            style={{ padding: "5px 10px", fontSize: 11,
                      background: "var(--pf-surface-2)",
                      color: "var(--pf-text)",
                      border: "1px solid var(--pf-border)",
                      borderRadius: 4, cursor: "pointer",
                      display: "flex", alignItems: "center", gap: 6 }}>
      <Icon size={12}/>
      {isFs ? "Exit Full Screen" : "Full Screen"}
    </button>
  );
}



// ────────────────────────────────────────────────────────────────────
//  Top-level component
// ────────────────────────────────────────────────────────────────────
export default function ProcessPlantA6View({ projectId }) {
  const [activeTab, setActiveTab] = useState("inputs");
  const [schema, setSchema] = useState(null);
  const [inputs, setInputs] = useState({});
  const [output, setOutput] = useState(null);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState(null);

  // 1) Load schema + last output + saved inputs in parallel
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    Promise.all([
      api.get(`/projects/${projectId}/process-plant/a6/inputs-schema`)
        .then(r => r.data).catch(() => null),
      api.get(`/projects/${projectId}/process-plant/a6/inputs`)
        .then(r => r.data).catch(() => ({})),
      api.get(`/projects/${projectId}/process-plant/a6/output`)
        .then(r => r.data?.output).catch(() => null),
    ]).then(([sch, savedInp, out]) => {
      if (cancelled) return;
      setSchema(sch);
      setInputs(savedInp || {});
      if (out && out.svg_drawing) setOutput(out);
    });
    return () => { cancelled = true; };
  }, [projectId]);

  // 2) Run engine
  const handleRun = useCallback(async () => {
    setErr(null); setRunning(true);
    try {
      // Persist inputs first
      await api.put(`/projects/${projectId}/process-plant/a6/inputs`,
        { inputs });
      // Run
      const r = await api.post(`/projects/${projectId}/process-plant/a6/run`);
      setOutput(r.data);
      setActiveTab("plot2d");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      let msg;
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail)) {
        msg = detail.map(d => (typeof d === "string" ? d
          : (d?.msg ? `${(d?.loc || []).join(".")} — ${d.msg}` : JSON.stringify(d)))).join("; ");
      } else if (detail && typeof detail === "object") {
        msg = detail.msg || JSON.stringify(detail);
      } else {
        msg = e.message || "Run failed";
      }
      setErr(msg);
    } finally {
      setRunning(false);
    }
  }, [projectId, inputs]);

  // 3) Update an input field
  const setField = useCallback((id, value) => {
    setInputs(prev => ({ ...prev, [id]: value }));
  }, []);

  return (
    <div data-testid="process-plant-a6" style={{
      display: "flex", flexDirection: "column", height: "100%",
      background: "var(--pf-bg)", color: "var(--pf-text)",
    }}>
      {/* Tabs */}
      <div style={{ display: "flex", gap: 0, borderBottom: "1px solid var(--pf-border)",
                     background: "var(--pf-surface)" }}>
        {TABS.map(t => {
          const Icon = t.icon;
          const active = activeTab === t.id;
          const disabled = (t.id !== "inputs") && !output;
          return (
            <button key={t.id}
                    data-testid={`a6-tab-${t.id}`}
                    onClick={() => !disabled && setActiveTab(t.id)}
                    disabled={disabled}
                    style={{
                      padding: "10px 16px", fontSize: 13, border: "none",
                      borderBottom: active
                        ? "2px solid var(--pf-spark)"
                        : "2px solid transparent",
                      background: active ? "var(--pf-surface-2)" : "transparent",
                      color: active ? "var(--pf-text)" : "var(--pf-text-muted)",
                      cursor: disabled ? "not-allowed" : "pointer",
                      opacity: disabled ? 0.45 : 1,
                      display: "flex", alignItems: "center", gap: 7,
                      fontWeight: active ? 600 : 400,
                    }}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
        <div style={{ flex: 1 }} />
        {output && (
          <div style={{ padding: "10px 14px", fontSize: 12,
                         color: "var(--pf-text-muted)",
                         display: "flex", alignItems: "center", gap: 10 }}>
            <span>{output.n_equipment} eqp</span>
            <span>{(output.plot_area_m2/10000).toFixed(1)} ha</span>
            {output.spacing_status === "compliant" ? (
              <span style={{ color: "var(--pf-ok)" }}>
                <CheckCircle2 size={12} style={{ verticalAlign: "-2px" }}/>
                OISD-118 ✓
              </span>
            ) : (
              <span style={{ color: "var(--pf-err)" }}>
                <AlertTriangle size={12} style={{ verticalAlign: "-2px" }}/>
                {output.violation_count} viol.
              </span>
            )}
          </div>
        )}
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflow: "auto" }}>
        {activeTab === "inputs" && (
          <InputsTab schema={schema} inputs={inputs} setField={setField}
                      handleRun={handleRun} running={running} err={err}
                      output={output} />
        )}
        {activeTab === "plot2d" && output && (
          <Plot2DTab svg={output.svg_drawing} projectId={projectId}
                      output={output}
                      onOutputRefresh={(newOutput) =>
                        setOutput((prev) => ({ ...prev, ...newOutput }))} />
        )}
        {activeTab === "plant3d" && output && (
          <Plant3DTab geometry={output.geometry_3d}
                         projectId={projectId} />
        )}
        {activeTab === "real3d" && output && (
          <Real3DTab geometry={output.geometry_3d}
                      geojson={output.geojson}
                      projectId={projectId} />
        )}
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────
//  Inputs tab
// ────────────────────────────────────────────────────────────────────
function InputsTab({ schema, inputs, setField, handleRun, running, err,
                       output }) {
  if (!schema) {
    return (
      <div style={{ padding: 24, color: "var(--pf-text-muted)" }}>
        <Loader2 className="pf-spin" size={14} /> Loading schema…
      </div>
    );
  }
  return (
    <div style={{ padding: 20, maxWidth: 900, margin: "0 auto" }}>
      <div style={{ marginBottom: 16, padding: 14,
                     background: "var(--pf-surface)",
                     border: "1px solid var(--pf-border)",
                     borderRadius: 6 }}>
        <div style={{ fontSize: 14, fontWeight: 600,
                       color: "var(--pf-text)", marginBottom: 6 }}>
          {schema.stage_name}
        </div>
        <div style={{ fontSize: 12, color: "var(--pf-text-muted)",
                       lineHeight: 1.55 }}>
          {schema.description}
        </div>
      </div>

      {schema.sections.map(sec => (
        <div key={sec.id} style={{ marginBottom: 18, padding: 14,
                                       background: "var(--pf-surface)",
                                       border: "1px solid var(--pf-border)",
                                       borderRadius: 6 }}>
          <div style={{ fontSize: 13, fontWeight: 600,
                         color: "var(--pf-text)", marginBottom: 10 }}>
            {sec.title}
          </div>
          <div style={{ display: "grid",
                         gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}>
            {sec.fields.map(f => (
              <InputField key={f.id} field={f}
                            value={inputs[f.id]}
                            onChange={(v) => setField(f.id, v)} />
            ))}
          </div>
        </div>
      ))}

      {err && (
        <div data-testid="a6-run-error"
             style={{ padding: 12, marginBottom: 12,
                       background: "rgba(122,31,31,0.15)",
                       border: "1px solid var(--pf-err)",
                       borderRadius: 6, color: "var(--pf-text)",
                       fontSize: 13 }}>
          <AlertTriangle size={14}
              style={{ verticalAlign: "-2px", marginRight: 6,
                        color: "var(--pf-err)" }}/>
          {err}
        </div>
      )}
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <button onClick={handleRun} disabled={running}
                data-testid="a6-run-button"
                style={{ padding: "10px 22px", fontSize: 13,
                          fontWeight: 600,
                          background: running
                            ? "var(--pf-graphite)"
                            : "var(--pf-spark)",
                          color: "#fff", border: "none",
                          borderRadius: 6,
                          cursor: running ? "wait" : "pointer",
                          display: "flex", alignItems: "center", gap: 8 }}>
          {running
            ? <><Loader2 className="pf-spin" size={14}/> Generating layout…</>
            : <><Play size={14}/> Run Layout Engine</>}
        </button>
        {output && (
          <div style={{ fontSize: 12, color: "var(--pf-text-muted)" }}>
            Last run: {output.n_equipment} items packed across{" "}
            {(output.plot_area_m2/10000).toFixed(1)} ha.
            {output.spacing_status === "compliant"
              ? <> All OISD-118 distances clear.</>
              : <> {output.violation_count} OISD-118 violation(s).</>}
          </div>
        )}
      </div>
    </div>
  );
}


function InputField({ field, value, onChange }) {
  const isSelect = field.type === "select";
  const inputStyle = {
    padding: "7px 9px", fontSize: 12, borderRadius: 4,
    border: "1px solid var(--pf-border)",
    background: "var(--pf-blueprint)", color: "var(--pf-text)",
    outline: "none", width: "100%",
  };
  return (
    <div>
      <label style={{ fontSize: 11, color: "var(--pf-text-muted)",
                       marginBottom: 4, display: "block" }}>
        {field.label}
        {field.unit && <span style={{ marginLeft: 4, opacity: 0.7 }}>
          ({field.unit})</span>}
      </label>
      {isSelect ? (
        <select value={value ?? field.default ?? ""}
                onChange={e => onChange(e.target.value)}
                data-testid={`a6-input-${field.id}`}
                style={inputStyle}>
          <option value="">— Auto —</option>
          {(field.options || []).map(o =>
            <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input type={field.type === "number" ? "number" : "text"}
               value={value ?? ""}
               placeholder={field.default != null
                              ? `default ${field.default}` : ""}
               step={field.step}
               min={field.min} max={field.max}
               onChange={e => onChange(
                 field.type === "number"
                   ? (e.target.value === "" ? null : Number(e.target.value))
                   : e.target.value)}
               data-testid={`a6-input-${field.id}`}
               style={inputStyle} />
      )}
      {field.help && (
        <div style={{ fontSize: 10, color: "var(--pf-text-dim)",
                       marginTop: 3, lineHeight: 1.45 }}>
          {field.help}
        </div>
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────
//  2D Plot Plan tab
// ────────────────────────────────────────────────────────────────────
function Plot2DTab({ svg, projectId, output, onOutputRefresh }) {
  const wrapRef = useRef(null);
  const svgHostRef = useRef(null);
  const [heatOn, setHeatOn] = useState(false);
  const [fixingTag, setFixingTag] = useState(null);
  const [fixMessage, setFixMessage] = useState(null);
  // AI plot plan (Nano Banana) state
  const [aiBusy, setAiBusy] = useState(false);
  const [aiUrl, setAiUrl] = useState(null);
  const [aiMeta, setAiMeta] = useState(null);
  const [aiErr, setAiErr] = useState(null);
  const [aiHires, setAiHires] = useState(false);
  const [viewMode, setViewMode] = useState("svg"); // "svg" | "ai"
  // Tag-map (HYSYS ↔ A.6) state
  const [tagMapBusy, setTagMapBusy] = useState(false);
  const [tagMapPreview, setTagMapPreview] = useState(null);
  const [tagMapOverrides, setTagMapOverrides] = useState({});
  const [tagMapErr, setTagMapErr] = useState(null);

  // Auto-load cached enrichment on mount (single GET; 404 → no-op).
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const s = await api.get(
          `/projects/${projectId}/process-plant/a6/plot-plan/enrich/status`);
        if (cancelled || !s.data || !s.data.generated_at) return;
        setAiMeta(s.data);
        const img = await api.get(
          `/projects/${projectId}/process-plant/a6/plot-plan/enrich/latest`,
          { responseType: "blob" });
        if (cancelled) return;
        setAiUrl(URL.createObjectURL(img.data));
        setViewMode("ai"); // prefer AI view when cached
      } catch (_e) { /* no enrichment yet — normal */ }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const openTagMapPreview = async () => {
    setTagMapBusy(true); setTagMapErr(null);
    setTagMapPreview(null); setTagMapOverrides({});
    try {
      const r = await api.get(
        `/projects/${projectId}/process-plant/a6/tag-map/preview`);
      if (r.data?.error) {
        setTagMapErr(r.data.message || r.data.error);
      } else {
        setTagMapPreview(r.data);
        // Seed overrides with every proposed match — user unchecks/edits
        // the ones they disagree with before applying.
        const seed = {};
        (r.data.matches || []).forEach(m => {
          if (m.confidence >= 0.7 && m.a6_tag !== m.sim_tag) {
            seed[m.a6_tag] = m.sim_tag;
          }
        });
        setTagMapOverrides(seed);
      }
    } catch (e) {
      setTagMapErr(e?.response?.data?.detail || e?.message
                     || "Failed to load tag-map preview.");
    } finally {
      setTagMapBusy(false);
    }
  };

  const applyTagMap = async () => {
    const overrides = Object.fromEntries(
      Object.entries(tagMapOverrides).filter(
        ([a6, sim]) => a6 && sim && a6 !== sim));
    if (Object.keys(overrides).length === 0) {
      setTagMapErr("No rename operations selected. Check at least one row.");
      return;
    }
    setTagMapBusy(true); setTagMapErr(null);
    try {
      const r = await api.post(
        `/projects/${projectId}/process-plant/a6/tag-map/apply`,
        { overrides });
      // Close modal + refresh the A.6 output
      setTagMapPreview(null); setTagMapOverrides({});
      if (typeof onOutputRefresh === "function") {
        await onOutputRefresh();
      }
      alert(r.data?.message
            || `Renamed ${Object.keys(overrides).length} equipment.`);
    } catch (e) {
      setTagMapErr(e?.response?.data?.detail || e?.message
                     || "Failed to apply tag map.");
    } finally {
      setTagMapBusy(false);
    }
  };

  const generateAiPlotPlan = async ({ isRegenerate = false } = {}) => {
    if (isRegenerate && aiUrl) {
      const ok = window.confirm(
        "This will REPLACE your current AI plot plan with a new one. "
        + "The current version cannot be restored. Continue?");
      if (!ok) return;
    }
    setAiBusy(true); setAiErr(null);
    try {
      const r = await api.post(
        `/projects/${projectId}/process-plant/a6/plot-plan/enrich?hires=${aiHires ? "true" : "false"}`,
        {}, { responseType: "blob", timeout: 240000 });
      if (aiUrl) URL.revokeObjectURL(aiUrl);
      setAiUrl(URL.createObjectURL(r.data));
      setAiMeta({
        model:     r.headers?.["x-enrichment-model"],
        elapsed_s: r.headers?.["x-enrichment-elapsed-s"],
        equipment_count: r.headers?.["x-equipment-count"],
        generated_at: new Date().toISOString(),
      });
      setViewMode("ai");
    } catch (e) {
      let msg = e?.response?.data?.detail || e?.message
                 || "AI plot plan generation failed. Try again.";
      if (e?.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          try { msg = JSON.parse(txt).detail || msg; }
          catch { msg = txt || msg; }
        } catch { /* keep default */ }
      }
      setAiErr(String(msg));
    } finally {
      setAiBusy(false);
    }
  };

  // Toggle the visibility of the `<g class="oisd-heatmap">` group
  // injected by the backend. Done imperatively because the SVG is
  // injected via dangerouslySetInnerHTML.
  useEffect(() => {
    const host = svgHostRef.current;
    if (!host) return;
    const g = host.querySelector(".oisd-heatmap");
    if (g) g.style.display = heatOn ? "inline" : "none";
  }, [heatOn, svg]);

  const heatmap = output?.oisd_heatmap || [];
  const counts = heatmap.reduce((acc, h) => {
    acc[h.status] = (acc[h.status] || 0) + 1;
    return acc;
  }, {});
  const violations = counts.violation || 0;
  const tight       = counts.tight     || 0;
  const ok          = counts.ok        || 0;
  // Worst-first list of items needing attention
  const needsFix = heatmap
    .filter(h => h.status === "violation" || h.status === "tight")
    .sort((a, b) => (a.ratio || 0) - (b.ratio || 0));

  const handleFix = async (tag) => {
    setFixMessage(null);
    setFixingTag(tag);
    try {
      const r = await api.post(
        `/projects/${projectId}/process-plant/a6/suggest-fix`,
        { tag });
      setFixMessage(r.data.message);
      onOutputRefresh && onOutputRefresh(r.data);
    } catch (e) {
      setFixMessage(e?.response?.data?.detail || e.message);
    } finally {
      setFixingTag(null);
    }
  };

  const onDownload = () => {
    const blob = new Blob([svg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `process-plant-a6-plot-plan.svg`;
    a.click(); URL.revokeObjectURL(url);
  };
  return (
    <div ref={wrapRef}
         data-testid="a6-plot2d-wrap"
         style={{ padding: 16, background: "var(--pf-bg)",
                   minHeight: "100%" }}>
      <div style={{ display: "flex", marginBottom: 10, gap: 8,
                     alignItems: "center", flexWrap: "wrap" }}>
        <button onClick={onDownload}
                data-testid="a6-download-svg"
                style={{ padding: "6px 12px", fontSize: 11,
                          background: "var(--pf-surface-2)",
                          color: "var(--pf-text)",
                          border: "1px solid var(--pf-border)",
                          borderRadius: 4, cursor: "pointer",
                          display: "flex", alignItems: "center", gap: 6 }}>
          <Download size={12}/> Download SVG
        </button>
        <FullscreenButton targetRef={wrapRef}
                            testId="a6-plot2d-fullscreen"/>
        <button onClick={() => setHeatOn(v => !v)}
                data-testid="a6-heatmap-toggle"
                title="Show OISD-118 spacing compliance halo around each item"
                style={{ padding: "6px 12px", fontSize: 11,
                          background: heatOn
                            ? "var(--pf-spark)" : "var(--pf-surface-2)",
                          color: heatOn ? "#fff" : "var(--pf-text)",
                          border: "1px solid var(--pf-border)",
                          borderRadius: 4, cursor: "pointer",
                          display: "flex", alignItems: "center", gap: 6 }}>
          <AlertTriangle size={12}/>
          {heatOn ? "Hide OISD Heat-map" : "Show OISD Heat-map"}
        </button>
        {heatOn && heatmap.length > 0 && (
          <div data-testid="a6-heatmap-summary"
               style={{ display: "flex", gap: 8, fontSize: 11,
                         color: "var(--pf-text)",
                         padding: "4px 8px",
                         background: "var(--pf-surface-2)",
                         border: "1px solid var(--pf-border)",
                         borderRadius: 4 }}>
            <span data-testid="a6-heatmap-ok">
              <span style={{ display: "inline-block", width: 8, height: 8,
                              background: "#22c55e",
                              borderRadius: "50%",
                              marginRight: 4 }}/>{ok} OK
            </span>
            <span data-testid="a6-heatmap-tight" style={{ color: "#f59e0b" }}>
              <span style={{ display: "inline-block", width: 8, height: 8,
                              background: "#f59e0b",
                              borderRadius: "50%",
                              marginRight: 4 }}/>{tight} Tight
            </span>
            <span data-testid="a6-heatmap-violations"
                  style={{ color: "#dc2626" }}>
              <span style={{ display: "inline-block", width: 8, height: 8,
                              background: "#dc2626",
                              borderRadius: "50%",
                              marginRight: 4 }}/>{violations} Violations
            </span>
          </div>
        )}
        <div style={{ fontSize: 11, color: "var(--pf-text-muted)" }}>
          A1 sheet · 1:{Math.round(
            1000 / (Math.min(841 / output.plot_width_m,
                              594 / output.plot_height_m))) || 1000}
        </div>
        <button onClick={openTagMapPreview}
                disabled={tagMapBusy}
                data-testid="a6-open-tag-map"
                title="Match A.6 equipment tags to your imported HYSYS/Aspen tags"
                style={{ padding: "6px 12px", fontSize: 11,
                          background: "var(--pf-surface-2)",
                          color: "var(--pf-text)",
                          border: "1px solid var(--pf-border)",
                          borderRadius: 4,
                          cursor: tagMapBusy ? "wait" : "pointer",
                          display: "flex", alignItems: "center", gap: 6 }}>
          {tagMapBusy
            ? <Loader2 size={12} className="animate-spin"/>
            : <MapIcon size={12}/>}
          Sync tags with simulator
        </button>
        {/* ─── AI Plot Plan (Nano Banana) controls ─── */}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8,
                       alignItems: "center", flexWrap: "wrap" }}>
          {aiUrl && (
            <div role="tablist"
                 data-testid="a6-plot-mode-toggle"
                 style={{ display: "flex",
                            border: "1px solid var(--pf-border)",
                            borderRadius: 4, overflow: "hidden" }}>
              <button onClick={() => setViewMode("svg")}
                      data-testid="a6-view-svg"
                      style={{ padding: "5px 10px", fontSize: 11,
                                background: viewMode === "svg"
                                  ? "var(--pf-spark)" : "transparent",
                                color: viewMode === "svg" ? "#fff"
                                  : "var(--pf-text)",
                                border: "none", cursor: "pointer" }}>
                Vector SVG
              </button>
              <button onClick={() => setViewMode("ai")}
                      data-testid="a6-view-ai"
                      style={{ padding: "5px 10px", fontSize: 11,
                                background: viewMode === "ai"
                                  ? "var(--pf-spark)" : "transparent",
                                color: viewMode === "ai" ? "#fff"
                                  : "var(--pf-text)",
                                border: "none",
                                borderLeft: "1px solid var(--pf-border)",
                                cursor: "pointer" }}>
                AI Industry Plot Plan
              </button>
            </div>
          )}
          <label style={{ fontSize: 10, color: "var(--pf-text-muted)",
                            display: "flex", alignItems: "center", gap: 4,
                            cursor: "pointer" }}
                 title="Use gemini-3-pro-image-preview for higher fidelity (60 s)">
            <input type="checkbox" checked={aiHires}
                   data-testid="a6-ai-hires"
                   onChange={e => setAiHires(e.target.checked)}
                   style={{ margin: 0 }}/>
            HiRes
          </label>
          <button onClick={() => generateAiPlotPlan({ isRegenerate: !!aiUrl })}
                  disabled={aiBusy}
                  data-testid={aiUrl ? "a6-regenerate-ai-plot-plan"
                                    : "a6-generate-ai-plot-plan"}
                  title={aiUrl
                    ? "Replace the current AI plot plan with a NEW one (destructive — click twice to confirm)"
                    : "Generate an industry-standard plot plan via Nano Banana (Gemini 3.1 Flash Image)"}
                  style={{ padding: "6px 12px", fontSize: 11,
                            background: aiBusy ? "var(--pf-surface-2)"
                              : (aiUrl ? "#dc2626" : "#8b5cf6"),
                            color: aiBusy ? "var(--pf-text-muted)"
                              : "#ffffff",
                            border: "1px solid " + (aiBusy
                              ? "var(--pf-border)"
                              : (aiUrl ? "#b91c1c" : "#7c3aed")),
                            borderRadius: 4,
                            cursor: aiBusy ? "wait" : "pointer",
                            display: "flex", alignItems: "center",
                            gap: 6, fontWeight: 600 }}>
            {aiBusy ? <Loader2 size={12} className="animate-spin"/>
                    : <Sparkles size={12}/>}
            {aiBusy
              ? "Generating (Nano Banana)…"
              : (aiUrl
                  ? "⚠ Regenerate (replaces current)"
                  : "Generate AI Plot Plan")}
          </button>
        </div>
      </div>

      {aiErr && (
        <div data-testid="a6-ai-plot-error"
             style={{ marginBottom: 10, padding: 10, fontSize: 11,
                       color: "#fca5a5",
                       background: "rgba(127,29,29,0.18)",
                       border: "1px solid #7f1d1d", borderRadius: 4 }}>
          <b>AI plot plan failed:</b> {aiErr}
        </div>
      )}
      {aiMeta && aiUrl && (
        <div data-testid="a6-ai-plot-meta"
             style={{ marginBottom: 10, padding: "4px 10px", fontSize: 10,
                       color: "var(--pf-text-muted)",
                       background: "var(--pf-surface-2)",
                       border: "1px solid var(--pf-border)",
                       borderRadius: 3,
                       display: "flex", gap: 12, flexWrap: "wrap" }}>
          <span>AI model: <b style={{ color: "#8b5cf6" }}>
            {aiMeta.model || "gemini-3.1-flash-image-preview"}</b></span>
          {aiMeta.elapsed_s && (
            <span>Latency: <b>{aiMeta.elapsed_s}s</b></span>
          )}
          {aiMeta.equipment_count && (
            <span>Equipment: <b>{aiMeta.equipment_count}</b></span>
          )}
          <span>Generated: {new Date(aiMeta.generated_at).toLocaleString()}</span>
        </div>
      )}

      {heatOn && (needsFix.length > 0 || fixMessage) && (
        <div data-testid="a6-fix-panel"
             style={{ marginBottom: 10, padding: 10,
                       background: "var(--pf-surface)",
                       border: "1px solid var(--pf-border)",
                       borderRadius: 4, maxHeight: 180,
                       overflowY: "auto" }}>
          {fixMessage && (
            <div data-testid="a6-fix-message"
                 style={{ fontSize: 11, color: "var(--pf-text)",
                           marginBottom: needsFix.length ? 8 : 0,
                           padding: "4px 8px",
                           background: "var(--pf-surface-2)",
                           border: "1px solid var(--pf-spark)",
                           borderRadius: 3 }}>
              {fixMessage}
            </div>
          )}
          {needsFix.length === 0 && !fixMessage ? null : (
            <div style={{ fontSize: 11, color: "var(--pf-text-muted)",
                           marginBottom: 6, fontWeight: 600 }}>
              Items needing attention (worst-first):
            </div>
          )}
          {needsFix.map(item => (
            <div key={item.tag}
                 data-testid={`a6-fix-row-${item.tag}`}
                 style={{ display: "flex", alignItems: "center",
                           gap: 8, padding: "4px 0",
                           fontSize: 11, color: "var(--pf-text)" }}>
              <span style={{ display: "inline-block", width: 8, height: 8,
                              background: item.color,
                              borderRadius: "50%" }}/>
              <span style={{ fontFamily: "'JetBrains Mono', monospace",
                              minWidth: 80 }}>{item.tag}</span>
              <span style={{ color: "var(--pf-text-muted)" }}>
                ratio <b style={{ color: item.color }}>
                  {item.ratio?.toFixed(2)}×
                </b>
                {item.nearest_tag && (
                  <> · vs {item.nearest_tag} ({item.nearest_dist_m} m /
                    needed {item.min_required_m} m)</>
                )}
              </span>
              <button onClick={() => handleFix(item.tag)}
                      disabled={fixingTag === item.tag}
                      data-testid={`a6-fix-btn-${item.tag}`}
                      style={{ marginLeft: "auto",
                                padding: "3px 10px", fontSize: 10,
                                background: "var(--pf-spark)",
                                color: "#fff",
                                border: "none",
                                borderRadius: 3,
                                cursor: fixingTag === item.tag
                                  ? "wait" : "pointer",
                                opacity: fixingTag === item.tag
                                  ? 0.6 : 1,
                                display: "flex", alignItems: "center",
                                gap: 4 }}>
                {fixingTag === item.tag
                  ? <Loader2 size={10} className="animate-spin"/>
                  : <CheckCircle2 size={10}/>}
                {fixingTag === item.tag ? "Fixing…" : "Suggest Fix"}
              </button>
            </div>
          ))}
        </div>
      )}

      {viewMode === "ai" && aiUrl ? (
        <div data-testid="a6-ai-plot-view"
             style={{ border: "1px solid var(--pf-border)",
                        borderRadius: 4, overflow: "hidden",
                        background: "#f8fafc",
                        height: "calc(100vh - 260px)",
                        minHeight: 520,
                        display: "flex",
                        justifyContent: "center", alignItems: "center" }}>
          <img src={aiUrl} alt="AI-generated industry plot plan"
               data-testid="a6-ai-plot-img"
               style={{ maxWidth: "100%", maxHeight: "100%",
                         objectFit: "contain" }}/>
        </div>
      ) : (
        <ApsPlotPlanViewer projectId={projectId}/>
      )}
      {tagMapPreview && (
        <TagMapModal
          preview={tagMapPreview}
          overrides={tagMapOverrides}
          onOverridesChange={setTagMapOverrides}
          onClose={() => { setTagMapPreview(null);
                             setTagMapOverrides({});
                             setTagMapErr(null); }}
          onApply={applyTagMap}
          busy={tagMapBusy}
          error={tagMapErr}/>
      )}
      {tagMapErr && !tagMapPreview && (
        <div data-testid="a6-tag-map-error"
             style={{ marginTop: 8, padding: 8, fontSize: 11,
                       color: "#fca5a5",
                       background: "rgba(127,29,29,0.18)",
                       border: "1px solid #7f1d1d",
                       borderRadius: 4 }}>
          {tagMapErr}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
//  TagMapModal — HYSYS ↔ A.6 tag-map preview & apply
// ────────────────────────────────────────────────────────────────────
function TagMapModal({ preview, overrides, onOverridesChange,
                        onClose, onApply, busy, error }) {
  const matches = preview?.matches || [];
  const unmatchedSim = preview?.unmatched_sim || [];
  const unmatchedA6 = preview?.unmatched_a6 || [];
  const summary = preview?.summary || {};
  const willRename = Object.entries(overrides || {}).filter(
    ([a6, sim]) => a6 && sim && a6 !== sim);

  const toggleMatch = (a6Tag, simTag) => {
    const next = { ...(overrides || {}) };
    if (next[a6Tag]) delete next[a6Tag];
    else next[a6Tag] = simTag;
    onOverridesChange(next);
  };

  return (
    <div data-testid="a6-tag-map-modal"
         style={{ position: "fixed", inset: 0,
                    background: "rgba(0,0,0,0.6)", zIndex: 1000,
                    display: "flex", alignItems: "center",
                    justifyContent: "center", padding: 20 }}
         onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
           style={{ background: "var(--pf-surface)",
                     border: "1px solid var(--pf-border)",
                     borderRadius: 8,
                     maxWidth: 960, width: "100%",
                     maxHeight: "90vh",
                     display: "flex", flexDirection: "column",
                     boxShadow: "0 20px 60px rgba(0,0,0,0.55)" }}>
        <div style={{ padding: "12px 18px",
                        borderBottom: "1px solid var(--pf-border)",
                        display: "flex", alignItems: "center",
                        justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700,
                            color: "var(--pf-text)" }}>
              Sync A.6 equipment tags with imported simulator
            </div>
            <div style={{ fontSize: 11, color: "var(--pf-text-muted)",
                            marginTop: 3 }}>
              {summary.matched}/{summary.total_a6} A.6 equipment matched ·
              {" "}{summary.high_confidence} high confidence ·
              {" "}{summary.review_needed} need review
            </div>
          </div>
          <button onClick={onClose}
                  data-testid="a6-tag-map-close"
                  style={{ padding: "4px 10px", fontSize: 11,
                            background: "transparent",
                            color: "var(--pf-text)",
                            border: "1px solid var(--pf-border)",
                            borderRadius: 4, cursor: "pointer" }}>Cancel</button>
        </div>

        <div style={{ flex: 1, overflow: "auto", padding: 14 }}>
          {error && (
            <div style={{ padding: 8, marginBottom: 10, fontSize: 11,
                            color: "#fca5a5",
                            background: "rgba(127,29,29,0.18)",
                            border: "1px solid #7f1d1d",
                            borderRadius: 4 }}>{error}</div>
          )}
          {matches.length === 0 && (
            <div style={{ padding: 12, fontSize: 12,
                            color: "var(--pf-text-muted)",
                            textAlign: "center" }}>
              No matches found. Nothing to sync.
            </div>
          )}
          {matches.length > 0 && (
            <table data-testid="a6-tag-map-table"
                   style={{ width: "100%", borderCollapse: "collapse",
                             fontSize: 11, color: "var(--pf-text)" }}>
              <thead style={{ position: "sticky", top: 0,
                                background: "var(--pf-surface)" }}>
                <tr style={{ borderBottom: "1px solid var(--pf-border)" }}>
                  <th style={{ padding: 6, textAlign: "left", width: 40 }}>Apply</th>
                  <th style={{ padding: 6, textAlign: "left" }}>A.6 tag (current)</th>
                  <th style={{ padding: 6, textAlign: "left" }}>Simulator tag</th>
                  <th style={{ padding: 6, textAlign: "left" }}>Kind</th>
                  <th style={{ padding: 6, textAlign: "left", width: 80 }}>Confidence</th>
                  <th style={{ padding: 6, textAlign: "left" }}>Reason</th>
                </tr>
              </thead>
              <tbody>
                {matches.map(m => {
                  const checked = !!overrides[m.a6_tag]
                                     && m.a6_tag !== m.sim_tag;
                  const identical = m.a6_tag === m.sim_tag;
                  return (
                    <tr key={m.a6_tag}
                        data-testid={`a6-tag-map-row-${m.a6_tag}`}
                        style={{ borderBottom: "1px solid var(--pf-border)",
                                   opacity: identical ? 0.55 : 1 }}>
                      <td style={{ padding: 6 }}>
                        <input type="checkbox"
                               checked={checked}
                               disabled={identical}
                               data-testid={`a6-tag-map-check-${m.a6_tag}`}
                               onChange={() => toggleMatch(m.a6_tag, m.sim_tag)}/>
                      </td>
                      <td style={{ padding: 6, fontFamily: "monospace" }}>
                        {m.a6_tag}
                        {m.a6_name && (
                          <div style={{ fontSize: 10,
                                          color: "var(--pf-text-muted)" }}>
                            {m.a6_name}</div>
                        )}
                      </td>
                      <td style={{ padding: 6, fontFamily: "monospace",
                                     color: identical ? "var(--pf-text-muted)"
                                            : "#22c55e" }}>
                        {identical ? "— same —" : m.sim_tag}
                        {m.sim_name && !identical && (
                          <div style={{ fontSize: 10,
                                          color: "var(--pf-text-muted)" }}>
                            {m.sim_name}</div>
                        )}
                      </td>
                      <td style={{ padding: 6 }}>{m.a6_type || "—"}</td>
                      <td style={{ padding: 6,
                                     color: m.confidence >= 0.85 ? "#22c55e"
                                            : m.confidence >= 0.7 ? "#facc15"
                                            : "#f97316" }}>
                        {Math.round(m.confidence * 100)}%
                      </td>
                      <td style={{ padding: 6,
                                     color: "var(--pf-text-muted)" }}>
                        {m.reason}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {(unmatchedSim.length > 0 || unmatchedA6.length > 0) && (
            <div style={{ marginTop: 14, display: "grid",
                            gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {unmatchedSim.length > 0 && (
                <div style={{ padding: 8,
                                background: "var(--pf-surface-2)",
                                border: "1px solid var(--pf-border)",
                                borderRadius: 4 }}>
                  <div style={{ fontSize: 11, fontWeight: 600,
                                  color: "#f97316", marginBottom: 4 }}>
                    Simulator equipment with no match ({unmatchedSim.length}):
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 14, fontSize: 10,
                                 color: "var(--pf-text-muted)" }}>
                    {unmatchedSim.map(u => (
                      <li key={u.tag}><b>{u.tag}</b> — {u.name || u.type}</li>
                    ))}
                  </ul>
                </div>
              )}
              {unmatchedA6.length > 0 && (
                <div style={{ padding: 8,
                                background: "var(--pf-surface-2)",
                                border: "1px solid var(--pf-border)",
                                borderRadius: 4 }}>
                  <div style={{ fontSize: 11, fontWeight: 600,
                                  color: "#f97316", marginBottom: 4 }}>
                    A.6 layout equipment with no simulator match ({unmatchedA6.length}):
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 14, fontSize: 10,
                                 color: "var(--pf-text-muted)" }}>
                    {unmatchedA6.map(u => (
                      <li key={u.tag}><b>{u.tag}</b> — {u.name || u.type}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ padding: "10px 18px",
                        borderTop: "1px solid var(--pf-border)",
                        display: "flex", alignItems: "center",
                        justifyContent: "space-between", gap: 10 }}>
          <div style={{ fontSize: 11, color: "var(--pf-text-muted)" }}>
            {willRename.length === 0
              ? "No renames selected — nothing to apply."
              : `Will rename ${willRename.length} equipment. `
                + "AI plot plan cache will be invalidated."}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={onClose}
                    style={{ padding: "6px 12px", fontSize: 11,
                              background: "transparent",
                              color: "var(--pf-text)",
                              border: "1px solid var(--pf-border)",
                              borderRadius: 4, cursor: "pointer" }}>
              Cancel</button>
            <button onClick={onApply}
                    data-testid="a6-tag-map-apply"
                    disabled={busy || willRename.length === 0}
                    style={{ padding: "6px 14px", fontSize: 11,
                              background: (busy || willRename.length === 0)
                                ? "var(--pf-surface-2)" : "#22c55e",
                              color: (busy || willRename.length === 0)
                                ? "var(--pf-text-muted)" : "#fff",
                              border: "1px solid " + (busy
                                ? "var(--pf-border)" : "#16a34a"),
                              borderRadius: 4,
                              cursor: busy ? "wait" : "pointer",
                              fontWeight: 600 }}>
              {busy
                ? <><Loader2 size={12} className="animate-spin"/> Applying…</>
                : `Apply ${willRename.length} rename${willRename.length===1?"":"s"}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── APS Viewer for the A.6 plot plan ─────────────────────
// Syncs the latest stage_outputs.A.6.svg_drawing into drawing_sheets
// (POST .../process-plant/a6/sync-to-aps) then renders the SVF2
// derivative via <ApsDrawingViewer>. Same source-of-truth as the
// Equipment Layout 3D scene (both read from process_plant_a6_outputs),
// so 2D and 3D views always stay visually consistent.
function ApsPlotPlanViewer({ projectId }) {
  const [sheetId, setSheetId] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 1) Push the latest A.6 SVG into drawing_sheets.
        await api.post(
          `/projects/${projectId}/process-plant/a6/sync-to-aps`);
        // 2) Kick off the APS Model Derivative job *now* so the
        //    user doesn't have to manually click "Generate". The
        //    backend short-circuits when the SVG content_hash is
        //    already cached, so this is cheap on revisits.
        try {
          await api.post(
            `/projects/${projectId}/aps/drawings/A6-PLOT-PLAN/translate`,
            { doc_kind: "plot-plan", theme: "dark" });
        } catch (_ignored) { /* viewer will pick up status via manifest */ }
        if (!cancelled) setSheetId("A6-PLOT-PLAN");
      } catch (e) {
        if (!cancelled) {
          setErr(e?.response?.data?.detail || e.message);
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);
  if (busy) return (
    <div data-testid="a6-aps-2d-loading"
         style={{ padding: 24, color: "var(--pf-muted)", fontSize: 12 }}>
      Preparing Autodesk Forge view of the plot plan…
    </div>);
  if (err) return (
    <div data-testid="a6-aps-2d-error"
         style={{ padding: 12, color: "#fca5a5", fontSize: 12,
                     background: "rgba(127,29,29,0.18)" }}>{err}</div>);
  return (
    <div data-testid="a6-aps-2d"
         style={{ height: "calc(100vh - 240px)", minHeight: 520,
                  display: "flex", flexDirection: "column" }}>
      <ApsDrawingViewer projectId={projectId} sheetId={sheetId}
          docKind="plot-plan"
          title="Preliminary Equipment Layout — Plot Plan"/>
    </div>
  );
}

// ─── Reusable SVG pan + zoom pane (mouse wheel + drag + Fit) ────
function SvgPanZoomPane({ svg, svgHostRef }) {
  const wrapRef = useRef(null);
  const innerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);

  const fit = useCallback(() => {
    const wrap = wrapRef.current;
    const inner = innerRef.current;
    if (!wrap || !inner) return;
    const svgEl = inner.querySelector("svg");
    if (!svgEl) return;
    const vb = svgEl.viewBox?.baseVal;
    const sw = (vb && vb.width)  || svgEl.clientWidth  || 1;
    const sh = (vb && vb.height) || svgEl.clientHeight || 1;
    const ww = wrap.clientWidth;
    const wh = wrap.clientHeight;
    const k = Math.min(ww / sw, wh / sh) * 0.96;
    setScale(k);
    // Centre the SVG inside the pane.
    setPan({ x: (ww - sw * k) / 2,
             y: (wh - sh * k) / 2 });
  }, []);

  // Auto-fit on first mount and whenever the underlying SVG changes.
  useEffect(() => {
    const t = setTimeout(fit, 50);
    return () => clearTimeout(t);
  }, [svg, fit]);

  const onWheel = (e) => {
    e.preventDefault();
    const wrap = wrapRef.current;
    if (!wrap) return;
    const r = wrap.getBoundingClientRect();
    const mx = e.clientX - r.left;
    const my = e.clientY - r.top;
    const delta = -e.deltaY * 0.0015;
    const newScale = Math.max(0.05, Math.min(20, scale * (1 + delta)));
    // Zoom toward cursor.
    const dx = (mx - pan.x) * (newScale / scale - 1);
    const dy = (my - pan.y) * (newScale / scale - 1);
    setScale(newScale);
    setPan({ x: pan.x - dx, y: pan.y - dy });
  };

  const onMouseDown = (e) => {
    if (e.button !== 0 && e.button !== 1) return;
    dragRef.current = { x: e.clientX, y: e.clientY,
                          px: pan.x, py: pan.y };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current) return;
    setPan({ x: dragRef.current.px + (e.clientX - dragRef.current.x),
             y: dragRef.current.py + (e.clientY - dragRef.current.y) });
  };
  const onMouseUp = () => { dragRef.current = null; };

  return (
    <div data-testid="a6-plot2d-panzoom"
         style={{ position: "relative",
                     border: "1px solid var(--pf-border)",
                     borderRadius: 4, overflow: "hidden",
                     background: "#FAFBFC",
                     height: "calc(100% - 50px)" }}>
      <div ref={wrapRef}
           onWheel={onWheel}
           onMouseDown={onMouseDown}
           onMouseMove={onMouseMove}
           onMouseUp={onMouseUp}
           onMouseLeave={onMouseUp}
           style={{ position: "absolute", inset: 0,
                       overflow: "hidden",
                       cursor: dragRef.current ? "grabbing" : "grab",
                       touchAction: "none" }}>
        <div ref={(el) => {
                 innerRef.current = el;
                 if (svgHostRef) { svgHostRef.current = el; }
              }}
             style={{ position: "absolute", top: 0, left: 0,
                         transform: `translate(${pan.x}px, ${pan.y}px) `
                                  + `scale(${scale})`,
                         transformOrigin: "0 0",
                         transition: dragRef.current
                                       ? "none" : "transform 80ms" }}
             dangerouslySetInnerHTML={{ __html: svg }} />
      </div>
      {/* Floating toolbar — Fit / + / − / scale readout. */}
      <div data-testid="a6-plot2d-toolbar"
           style={{ position: "absolute", bottom: 14, left: "50%",
                       transform: "translateX(-50%)", zIndex: 10,
                       display: "flex", gap: 4, padding: "4px 6px",
                       borderRadius: 999,
                       background: "rgba(15,23,41,0.85)",
                       backdropFilter: "blur(10px)",
                       border: "1px solid rgba(255,255,255,0.1)",
                       boxShadow: "0 6px 24px rgba(0,0,0,0.35)" }}
           onMouseDown={(e) => e.stopPropagation()}>
        <button onClick={() => setScale((s) => Math.max(0.05, s * 0.8))}
                data-testid="a6-plot2d-zoom-out"
                style={pillBtnStyle()}>−</button>
        <button onClick={fit}
                data-testid="a6-plot2d-fit"
                title="Fit to page"
                style={{ ...pillBtnStyle(), minWidth: 60, fontSize: 11 }}>
          Fit · {Math.round(scale * 100)}%
        </button>
        <button onClick={() => setScale((s) => Math.min(20, s * 1.25))}
                data-testid="a6-plot2d-zoom-in"
                style={pillBtnStyle()}>+</button>
      </div>
    </div>
  );
}

function pillBtnStyle() {
  return {
    background: "transparent", color: "#e2e8f0",
    border: "1px solid rgba(255,255,255,0.12)",
    padding: "5px 12px", borderRadius: 999, cursor: "pointer",
    fontSize: 13, lineHeight: 1, fontFamily: "monospace",
  };
}


// ────────────────────────────────────────────────────────────────────
//  3D Plant tab (Babylon, no Cesium)
// ────────────────────────────────────────────────────────────────────
function Plant3DTab({ geometry, projectId }) {
  // APS-backed 3D Plant view. Pushes the latest A.6 geometry_3d
  // through the OBJ → SVF2 pipeline once on mount, then hands off to
  // <ApsDrawingViewer> which exposes APS' native orbit / pan / zoom /
  // explode / measure tools. ``geometry`` prop is kept on the
  // signature so callers don't have to change — it is unused here
  // (single source of truth lives on the backend).
  if (geometry === undefined) { /* keep prop in signature */ }
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await api.post(
          `/projects/${projectId}/process-plant/a6/sync-3d-to-aps`);
        if (!cancelled) setReady(true);
      } catch (e) {
        if (!cancelled)
          setErr(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);
  if (busy) return (
    <div data-testid="a6-aps-3d-loading"
         style={{ padding: 24, color: "var(--pf-muted)", fontSize: 12 }}>
      Translating 3D plant layout to Autodesk SVF2 — usually 30-90 s
      on first open, instant from cache afterwards…
    </div>);
  if (err) return (
    <div data-testid="a6-aps-3d-error"
         style={{ padding: 12, color: "#fca5a5", fontSize: 12,
                     background: "rgba(127,29,29,0.18)" }}>{err}</div>);
  if (!ready) return null;
  return (
    <div data-testid="a6-aps-3d-wrap"
         style={{ height: "calc(100vh - 240px)", minHeight: 520,
                  display: "flex", flexDirection: "column" }}>
      <ApsDrawingViewer projectId={projectId} sheetId="A6-3D-PLANT"
          docKind="plant3d"
          title="Preliminary 3D Plant Layout"/>
    </div>
  );
}

// ── Legacy Babylon 3D Plant (retained for diagnostics; the export
//    above + <ApsDrawingViewer> have replaced this on the user path.)
function Plant3DTabBabylon({ geometry }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const sceneRef = useRef(null);
  const [mode, setMode] = useState("orbit");
  const [hover, setHover] = useState(null);   // {x, y, label} or null

  useEffect(() => {
    if (!canvasRef.current || !geometry) return;
    const engine = new BABYLON.Engine(canvasRef.current, true,
      { preserveDrawingBuffer: true, stencil: true });
    engineRef.current = engine;
    const scene = build3DScene(engine, geometry, mode === "walk");
    sceneRef.current = scene;
    _attachHoverTooltip(scene, canvasRef.current, setHover);
    engine.runRenderLoop(() => scene.render());
    // Auto-fit after meshes are built so the default framing shows
    // the whole plot — fixes the "stuck inside the equipment lanes"
    // first impression.
    setTimeout(() => fitAllInScene(scene), 80);
    const onResize = () => engine.resize();
    window.addEventListener("resize", onResize);
    document.addEventListener("fullscreenchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      document.removeEventListener("fullscreenchange", onResize);
      sceneRef.current = null;
      try { scene.dispose(); } catch { /* ignored */ }
      try { engine.dispose(); } catch { /* ignored */ }
    };
  }, [geometry, mode]);

  return (
    <div ref={wrapRef}
         data-testid="a6-plant-3d-wrap"
         style={{ position: "relative", height: "calc(100vh - 200px)",
                   minHeight: 500, background: "#0b1320" }}>
      <canvas ref={canvasRef}
              data-testid="a6-plant-3d-canvas"
              style={{ width: "100%", height: "100%",
                        display: "block", outline: "none" }}/>
      {hover && (
        <div data-testid="a6-3d-hover-tooltip"
             style={{ position: "absolute",
                       left: Math.min(hover.x + 14,
                                       (canvasRef.current?.clientWidth || 800) - 260),
                       top: hover.y + 14,
                       padding: "5px 10px",
                       background: "rgba(15, 19, 28, 0.95)",
                       border: "1px solid var(--pf-spark)",
                       color: "var(--pf-text)",
                       fontSize: 11, borderRadius: 4,
                       pointerEvents: "none",
                       fontFamily: "'JetBrains Mono', monospace",
                       maxWidth: 250, zIndex: 10 }}>
          {hover.label}
        </div>
      )}
      <div style={{ position: "absolute", top: 10, left: 10,
                     display: "flex", gap: 6 }}>
        {[
          { id: "orbit", label: "Orbit" },
          { id: "walk", label: "Walk Through" },
        ].map(m => (
          <button key={m.id} onClick={() => setMode(m.id)}
                  data-testid={`a6-3d-mode-${m.id}`}
                  style={{
                    padding: "5px 12px", fontSize: 11,
                    background: mode === m.id
                      ? "var(--pf-spark)" : "var(--pf-surface-2)",
                    color: mode === m.id ? "#fff" : "var(--pf-text)",
                    border: "1px solid var(--pf-border)",
                    borderRadius: 4, cursor: "pointer",
                  }}>
            {m.label}
          </button>
        ))}
        <FullscreenButton targetRef={wrapRef}
                            testId="a6-plant-3d-fullscreen"/>
        <button onClick={() => fitAllInScene(sceneRef.current)}
                data-testid="a6-plant-3d-fit-all"
                title="Fit all equipment in view"
                style={{ padding: "5px 12px", fontSize: 11,
                          background: "var(--pf-surface-2)",
                          color: "var(--pf-text)",
                          border: "1px solid var(--pf-border)",
                          borderRadius: 4, cursor: "pointer" }}>
          Fit All
        </button>
      </div>
      {mode === "orbit" && (
        <ViewCube getScene={() => sceneRef.current}
                    testIdPrefix="a6-plant-3d-viewcube"/>
      )}
      {mode === "walk" && (
        <div style={{ position: "absolute", bottom: 10, left: 10,
                       padding: "6px 12px", fontSize: 11,
                       background: "rgba(15, 19, 28, 0.85)",
                       color: "var(--pf-text)", borderRadius: 4 }}>
          W/A/S/D to move · Mouse to look around · Space jump
        </div>
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────
//  3D + Real World tab (Babylon + Cesium photoreal tiles)
// ────────────────────────────────────────────────────────────────────
const CESIUM_TOKEN    = process.env.REACT_APP_CESIUM_ION_TOKEN || "";
const CESIUM_ASSET_ID = process.env.REACT_APP_CESIUM_ION_ASSET_ID || "2275207";
const HAS_CESIUM_TOKEN = CESIUM_TOKEN.length > 10;

function Real3DTab({ geometry, geojson, projectId }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const sceneRef = useRef(null);
  const tilesRef = useRef(null);
  const [mode, setMode] = useState("orbit");
  const [hover, setHover] = useState(null);
  const [fitStatus, setFitStatus] = useState(null);
  // Feb 2026 — parity toggle. When ON (default) Real3D loads the SAME
  // .glb binary that Plant3D pushes to APS, so both tabs render pixel-
  // identical geometry. When OFF, falls back to the legacy Babylon
  // JSON interpreter (retained for diagnostics only).
  const [useGlb, setUseGlb] = useState(true);
  const [glbLoadError, setGlbLoadError] = useState(null);

  useEffect(() => {
    if (!canvasRef.current || !geometry || !geojson) return;
    const engine = new BABYLON.Engine(canvasRef.current, true,
      { preserveDrawingBuffer: true, stencil: true });
    // Build the base scene — with hidePlate=true we get the ground
    // plane + lighting but skip the placeholder equipment blocks.
    // When useGlb is on we import the glTF meshes over this scene.
    const scene = build3DScene(engine, useGlb ? {meshes: []} : geometry,
                                 mode === "walk",
                                 /*hidePlate*/ true,
                                 /*rightHanded*/ true);
    sceneRef.current = scene;
    _attachHoverTooltip(scene, canvasRef.current, setHover);

    // If useGlb is on, fetch the .glb and import into this scene.
    // Cancellation-safe: if StrictMode / unmount fires the cleanup
    // BEFORE the async import returns, we skip the mutation instead
    // of raising (which used to permanently flip the user into
    // Diagnostic mode on every tab visit — a real UX papercut).
    let cancelled = false;
    if (useGlb && projectId) {
      setGlbLoadError(null);
      (async () => {
        try {
          const r = await api.get(
            `/projects/${projectId}/process-plant/a6/geometry.glb`,
            { responseType: "arraybuffer", timeout: 60000 });
          if (cancelled || scene.isDisposed) return;
          const blob = new Blob([r.data], { type: "model/gltf-binary" });
          const url = URL.createObjectURL(blob);
          await BABYLON.SceneLoader.ImportMeshAsync(
            "", "", url, scene, undefined, ".glb");
          URL.revokeObjectURL(url);
          if (cancelled || scene.isDisposed) return;
          setTimeout(() => {
            if (!scene.isDisposed) fitAllInScene(scene);
          }, 200);
        } catch (e) {
          // Ignore errors on unmounted scenes — they're expected and
          // not user-visible.
          if (cancelled || scene.isDisposed) return;
          // Only treat network / parse errors as a real fallback.
          // Cancelled AbortErrors and "scene disposed" errors don't
          // count.
          const msg = e?.message || String(e);
          if (msg.toLowerCase().includes("cancel") ||
              msg.toLowerCase().includes("disposed") ||
              msg.toLowerCase().includes("aborted")) return;
          console.warn("[A6 Real3D] .glb import failed, "
                             + "falling back to JSON:", msg);
          setGlbLoadError(msg);
          setUseGlb(false);
        }
      })();
    }

    // Attach Cesium 3D Tiles using GeoJSON anchor
    const lat = geojson?.anchor?.lat;
    const lon = geojson?.anchor?.lon;
    let tiles = null;
    let tilesObserver = null;
    if (HAS_CESIUM_TOKEN && lat && lon && Math.abs(lat) > 0.1) {
      try {
        tiles = createCesiumIonTiles({
          scene, camera: scene.activeCamera,
          lat, lon, height: 0,
          token: CESIUM_TOKEN,
          assetId: CESIUM_ASSET_ID,
        });
        tilesRef.current = tiles;
        tilesObserver = scene.onBeforeRenderObservable.add(() => {
          try { tiles?.update?.(); } catch { /* ignored */ }
        });
      } catch (e) {
        console.warn("[A6 Real3D] Cesium tiles disabled:", e?.message);
      }
    }

    engine.runRenderLoop(() => scene.render());
    // Auto-fit on first scene mount so the user starts with all the
    // equipment in view rather than inside one of the racks.
    setTimeout(() => fitAllInScene(scene), 120);

    const onResize = () => engine.resize();
    window.addEventListener("resize", onResize);
    document.addEventListener("fullscreenchange", onResize);
    return () => {
      cancelled = true;
      window.removeEventListener("resize", onResize);
      document.removeEventListener("fullscreenchange", onResize);
      sceneRef.current = null;
      tilesRef.current = null;
      try {
        if (tilesObserver) scene.onBeforeRenderObservable.remove(tilesObserver);
      } catch { /* ignored */ }
      try { tiles?.dispose?.(); } catch { /* ignored */ }
      try { scene.dispose(); } catch { /* ignored */ }
      try { engine.dispose(); } catch { /* ignored */ }
    };
  }, [geometry, geojson, mode, useGlb, projectId]);

  // ── Fit-to-Terrain ───────────────────────────────────────────
  // Sample the Cesium tileset's ground elevation under the scene
  // origin and translate the plantRoot up by that amount, so the
  // designed plant sits ON the photoreal terrain instead of below /
  // above it.
  const fitToTerrain = useCallback(() => {
    const scene = sceneRef.current;
    const tiles = tilesRef.current;
    if (!scene) { setFitStatus("Scene not ready."); return; }
    if (!tiles) {
      setFitStatus("Cesium tiles not loaded — no terrain to fit to.");
      return;
    }
    setFitStatus("Sampling terrain…");
    // Try sampling at several spots inside the plot (centre + 4 corners
    // scaled down to 25 % of plot half-width to avoid sampling beyond
    // the tile load area) and use the median elevation, which is
    // robust to occasional misses on building rooftops.
    const halfW = (geometry.plot_width_m  || 200) * 0.25;
    const halfH = (geometry.plot_height_m || 200) * 0.25;
    const samples = [
      [0, 0], [halfW, halfH], [-halfW, halfH],
      [halfW, -halfH], [-halfW, -halfH],
    ];
    const hits = [];
    for (const [sx, sz] of samples) {
      const y = tiles.groundElevationAt(sx, sz);
      if (y !== null && Number.isFinite(y)) hits.push(y);
    }
    if (hits.length === 0) {
      setFitStatus(
        "No terrain hit — tiles still loading. Wait a few seconds, "
        + "pan around, then try again.");
      return;
    }
    hits.sort((a, b) => a - b);
    const median = hits[Math.floor(hits.length / 2)];
    const plantRoot = scene.getNodeByName("plantRoot");
    if (!plantRoot) {
      setFitStatus("plantRoot not found.");
      return;
    }
    plantRoot.position.y = median;
    setFitStatus(
      `Fitted to terrain · base elevation = ${median.toFixed(2)} m  `
      + `(median of ${hits.length} samples).`);
  }, [geometry]);

  const hasCoords = (geojson?.anchor?.lat || 0) !== 0
                     && (geojson?.anchor?.lon || 0) !== 0;

  return (
    <div ref={wrapRef}
         data-testid="a6-real3d-wrap"
         style={{ position: "relative", height: "calc(100vh - 200px)",
                   minHeight: 500, background: "#0b1320" }}>
      <canvas ref={canvasRef}
              data-testid="a6-real3d-canvas"
              style={{ width: "100%", height: "100%",
                        display: "block", outline: "none" }}/>
      {hover && (
        <div data-testid="a6-real3d-hover-tooltip"
             style={{ position: "absolute",
                       left: Math.min(hover.x + 14,
                                       (canvasRef.current?.clientWidth || 800) - 260),
                       top: hover.y + 14,
                       padding: "5px 10px",
                       background: "rgba(15, 19, 28, 0.95)",
                       border: "1px solid var(--pf-spark)",
                       color: "var(--pf-text)",
                       fontSize: 11, borderRadius: 4,
                       pointerEvents: "none",
                       fontFamily: "'JetBrains Mono', monospace",
                       maxWidth: 250, zIndex: 10 }}>
          {hover.label}
        </div>
      )}
      <div style={{ position: "absolute", top: 10, left: 10,
                     display: "flex", gap: 6 }}>
        {[
          { id: "orbit", label: "Orbit" },
          { id: "walk", label: "Walk Through" },
        ].map(m => (
          <button key={m.id} onClick={() => setMode(m.id)}
                  data-testid={`a6-real3d-mode-${m.id}`}
                  style={{
                    padding: "5px 12px", fontSize: 11,
                    background: mode === m.id
                      ? "var(--pf-spark)" : "var(--pf-surface-2)",
                    color: mode === m.id ? "#fff" : "var(--pf-text)",
                    border: "1px solid var(--pf-border)",
                    borderRadius: 4, cursor: "pointer",
                  }}>
            {m.label}
          </button>
        ))}
        <FullscreenButton targetRef={wrapRef}
                            testId="a6-real3d-fullscreen"/>
        <button onClick={() => fitAllInScene(sceneRef.current)}
                data-testid="a6-real3d-fit-all"
                title="Fit all equipment in view"
                style={{ padding: "5px 12px", fontSize: 11,
                          background: "var(--pf-surface-2)",
                          color: "var(--pf-text)",
                          border: "1px solid var(--pf-border)",
                          borderRadius: 4, cursor: "pointer" }}>
          Fit All
        </button>
        <button onClick={fitToTerrain}
                data-testid="a6-real3d-fit-to-terrain"
                title="Snap plant to Cesium terrain elevation"
                style={{ padding: "5px 12px", fontSize: 11,
                          background: "var(--pf-surface-2)",
                          color: "var(--pf-text)",
                          border: "1px solid var(--pf-border)",
                          borderRadius: 4, cursor: "pointer",
                          display: "flex", alignItems: "center", gap: 6 }}>
          <MapIcon size={12}/> Fit to Terrain
        </button>
        <label data-testid="a6-real3d-parity-toggle"
                  title="When ON, Real3D loads the same .glb binary that Plant3D uses, guaranteeing pixel-identical rendering between both tabs. Turn OFF only for diagnostics."
                  style={{ padding: "5px 10px", fontSize: 11,
                              background: useGlb ? "#10b98122" : "#f59e0b22",
                              color: "var(--pf-text)",
                              border: `1px solid ${useGlb ? "#10b981" : "#f59e0b"}`,
                              borderRadius: 4, cursor: "pointer",
                              display: "flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={useGlb}
                    onChange={(e) => setUseGlb(e.target.checked)}
                    style={{ margin: 0 }}/>
          {useGlb ? "🔒 Locked to Plant3D geometry"
                        : "⚠ Diagnostic mode (JSON path)"}
        </label>
      </div>
      {glbLoadError && (
        <div data-testid="a6-real3d-glb-error"
             style={{ position: "absolute", top: 60, right: 10,
                       padding: "6px 12px", fontSize: 11,
                       background: "rgba(239, 68, 68, 0.9)",
                       color: "#fff", borderRadius: 4, maxWidth: 320 }}>
          .glb load failed ({glbLoadError.slice(0, 80)}) — fell back to JSON. Both tabs may render differently.
        </div>
      )}
      {mode === "orbit" && (
        <ViewCube getScene={() => sceneRef.current}
                    testIdPrefix="a6-real3d-viewcube"/>
      )}
      {fitStatus && (
        <div data-testid="a6-real3d-fit-status"
             style={{ position: "absolute", bottom: 10, left: 10,
                       padding: "6px 12px", fontSize: 11,
                       background: "rgba(15, 19, 28, 0.92)",
                       color: "var(--pf-text)",
                       border: "1px solid var(--pf-spark)",
                       borderRadius: 4, maxWidth: 480 }}>
          {fitStatus}
        </div>
      )}
      {!hasCoords && (
        <div style={{ position: "absolute", top: 10, right: 10,
                       padding: "6px 10px", fontSize: 11,
                       background: "rgba(122,31,31,0.85)",
                       color: "#fff", borderRadius: 4, maxWidth: 280 }}>
          A.1 site_coordinates missing — Cesium tiles cannot anchor.
          Plant geometry shown at scene origin.
        </div>
      )}
      {hasCoords && !HAS_CESIUM_TOKEN && (
        <div style={{ position: "absolute", top: 10, right: 10,
                       padding: "6px 10px", fontSize: 11,
                       background: "rgba(122,80,31,0.9)",
                       color: "#fff", borderRadius: 4, maxWidth: 280 }}>
          Cesium Ion token missing — set REACT_APP_CESIUM_ION_TOKEN to
          enable photoreal earth tiles. Plant geometry rendered solo.
        </div>
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────
//  Babylon scene builder — shared by both 3D tabs
// ────────────────────────────────────────────────────────────────────
// ─────────────── Wave CD — exports for re-use in D.2 view ───────────────
// These tabs encapsulate the Babylon 3D scene + Cesium real-world tab
// and are reused 1:1 from the D.2 (matured plot plan) panel.
export { Plant3DTab, Real3DTab, build3DScene, FullscreenButton };

function build3DScene(engine, geometry, walkMode, hideGroundPlate = false,
                       rightHanded = false) {
  const scene = new BABYLON.Scene(engine);
  if (rightHanded) scene.useRightHandedSystem = true;
  // CH.2 — caller-tunable view options so the J.1 federated viewer
  // can use a tighter ground plate + closer camera without affecting
  // the D.2 view (which has colored zones to define the plot).
  const vopts = geometry.viewOptions || {};
  const groundPadding   = vopts.groundPadding   ?? 400;
  const camRadiusFactor = vopts.cameraRadiusFactor ?? 1.6;
  const camRadiusAbs    = vopts.cameraRadius    ?? null;
  const camBeta         = vopts.cameraBeta      ?? (Math.PI / 3.4);
  const camTarget       = vopts.cameraTarget    ?? [0, 5, 0];
  const fogStartFactor  = vopts.fogStartFactor  ?? 2;
  const fogEndFactor    = vopts.fogEndFactor    ?? 6;
  // Wave CB.2 — much brighter sky-blue background so equipment reads
  // against the plate instead of merging into a near-black void.
  scene.clearColor = new BABYLON.Color4(0.62, 0.74, 0.86, 1);
  scene.fogMode = BABYLON.Scene.FOGMODE_LINEAR;
  scene.fogColor = new BABYLON.Color3(0.62, 0.74, 0.86);
  scene.fogStart = Math.max(geometry.plot_width_m, geometry.plot_height_m) * fogStartFactor;
  scene.fogEnd = Math.max(geometry.plot_width_m, geometry.plot_height_m) * fogEndFactor;

  // Sun (hemispheric for ambient + directional for shadows/highlight)
  const sun = new BABYLON.HemisphericLight("sun",
    new BABYLON.Vector3(0.3, 1, 0.4), scene);
  sun.intensity = 1.15;
  sun.groundColor = new BABYLON.Color3(0.45, 0.40, 0.35);
  const dir = new BABYLON.DirectionalLight("dir",
    new BABYLON.Vector3(-0.4, -1, -0.6), scene);
  dir.intensity = 0.85;
  dir.position = new BABYLON.Vector3(
    geometry.plot_width_m * 0.3, geometry.plot_height_m, geometry.plot_height_m * 0.3
  );

  // Ground plate — brighter concrete-grey so the plant reads against it.
  if (!hideGroundPlate) {
    const plate = BABYLON.MeshBuilder.CreateGround("ground", {
      width: geometry.plot_width_m + groundPadding,
      height: geometry.plot_height_m + groundPadding,
      subdivisions: 4,
    }, scene);
    const plateMat = new BABYLON.StandardMaterial("plateMat", scene);
    plateMat.diffuseColor = BABYLON.Color3.FromHexString("#94a3a8");
    plateMat.specularColor = new BABYLON.Color3(0.05, 0.05, 0.05);
    plate.material = plateMat;
    plate.position.y = -0.05;
  }

  // Group all plant content under a named TransformNode so the
  // "Fit to Terrain" button can vertically translate everything in
  // a single operation (and ViewCube / future tools can address the
  // whole plant assembly by name).
  const plantRoot = new BABYLON.TransformNode("plantRoot", scene);

  // Zone colored patches
  const ZONE_COLOR = {
    process: "#7a1f1f", tank_farm: "#92580a", utility: "#0e7c6b",
    admin: "#1e4e8c", crf: "#2e70ad", substation: "#143a6b",
    flare: "#f26b1f", lpg_farm: "#7a1f1f",
  };
  (geometry.zones || []).forEach(z => {
    const m = BABYLON.MeshBuilder.CreateGround(`zone_${z.name}`, {
      width: z.width_m, height: z.depth_m,
    }, scene);
    m.position.x = z.x; m.position.z = z.z; m.position.y = 0;
    const mat = new BABYLON.StandardMaterial(`mat_z_${z.name}`, scene);
    mat.diffuseColor = BABYLON.Color3.FromHexString(
      ZONE_COLOR[z.name] || "#4b5563");
    mat.alpha = 0.45;
    mat.specularColor = new BABYLON.Color3(0, 0, 0);
    m.material = mat;
    m.isPickable = false;
    m.parent = plantRoot;
  });

  // Equipment — per-type composite meshes built via _buildEquipMesh.
  // Each mesh's metadata carries (tag, name, equip_type, zone) so the
  // pointer-move handler can drive the hover tooltip overlay.
  (geometry.equipment || []).forEach(eq => {
    const mesh = _buildEquipMesh(eq, scene);
    if (!mesh) return;
    mesh.position.x = eq.x;
    mesh.position.z = eq.z;
    if (eq.rotation_y_deg) {
      mesh.rotation.y = (eq.rotation_y_deg * Math.PI) / 180;
    }
    mesh.metadata = {
      tag: eq.tag, name: eq.name,
      equip_type: eq.equip_type, zone: eq.zone,
    };
    // Propagate metadata to children so pointer-picks on sub-meshes
    // still surface the parent's identity.
    mesh.getChildMeshes().forEach(c => { c.metadata = mesh.metadata; });
    mesh.parent = plantRoot;
  });

  // Pipe racks
  (geometry.pipe_racks || []).forEach(pr => {
    for (let i = 0; i < pr.polyline.length - 1; i++) {
      const a = pr.polyline[i], b = pr.polyline[i + 1];
      const dx = b[0] - a[0]; const dz = b[2] - a[2];
      const len = Math.hypot(dx, dz);
      const mesh = BABYLON.MeshBuilder.CreateBox(`pr_${i}`, {
        width: pr.width_m, height: pr.height_m, depth: len,
      }, scene);
      mesh.position.x = (a[0] + b[0]) / 2;
      mesh.position.z = (a[2] + b[2]) / 2;
      mesh.position.y = pr.elevation_m + pr.height_m / 2;
      mesh.rotation.y = Math.atan2(dx, dz);
      const mat = new BABYLON.StandardMaterial(`mat_pr_${i}`, scene);
      mat.diffuseColor = BABYLON.Color3.FromHexString("#92580a");
      mat.specularColor = new BABYLON.Color3(0.1, 0.1, 0.1);
      mesh.material = mat;
      mesh.parent = plantRoot;
    }
  });

  // Roads as thin flat strips
  (geometry.roads || []).forEach((r, idx) => {
    for (let i = 0; i < r.polyline.length - 1; i++) {
      const a = r.polyline[i], b = r.polyline[i + 1];
      const dx = b[0] - a[0]; const dz = b[2] - a[2];
      const len = Math.hypot(dx, dz);
      if (len < 0.5) continue;
      const mesh = BABYLON.MeshBuilder.CreateBox(`road_${idx}_${i}`, {
        width: r.width_m, height: 0.3, depth: len,
      }, scene);
      mesh.position.x = (a[0] + b[0]) / 2;
      mesh.position.z = (a[2] + b[2]) / 2;
      mesh.position.y = 0.05;
      mesh.rotation.y = Math.atan2(dx, dz);
      const mat = new BABYLON.StandardMaterial(`mat_road_${idx}_${i}`, scene);
      mat.diffuseColor = BABYLON.Color3.FromHexString("#334155");
      mat.specularColor = new BABYLON.Color3(0, 0, 0);
      mesh.material = mat;
      mesh.parent = plantRoot;
    }
  });

  // Camera
  const canvas = engine.getRenderingCanvas();
  if (walkMode) {
    const cam = new BABYLON.UniversalCamera("walk",
      new BABYLON.Vector3(0, 1.7, -geometry.plot_height_m / 2 - 30),
      scene);
    cam.setTarget(new BABYLON.Vector3(0, 1.7, 0));
    cam.attachControl(canvas, true);
    cam.speed = 1.2;
    cam.angularSensibility = 1500;
    cam.minZ = 0.1; cam.maxZ = 30000;
    // WASD keys
    cam.keysUp.push(87); cam.keysDown.push(83);
    cam.keysLeft.push(65); cam.keysRight.push(68);
    cam.applyGravity = true;
    cam.checkCollisions = true;
    scene.gravity = new BABYLON.Vector3(0, -9.81 / 60, 0);
    scene.collisionsEnabled = true;
    cam.ellipsoid = new BABYLON.Vector3(0.8, 0.9, 0.8);
  } else {
    const orbit = new BABYLON.ArcRotateCamera("orbit",
      -Math.PI / 4, camBeta,
      camRadiusAbs ?? Math.max(geometry.plot_width_m, geometry.plot_height_m) * camRadiusFactor,
      new BABYLON.Vector3(camTarget[0], camTarget[1], camTarget[2]), scene);
    orbit.attachControl(canvas, true);
    orbit.lowerRadiusLimit = 30;
    orbit.upperRadiusLimit = 50000;
    orbit.minZ = 0.5; orbit.maxZ = 100000;
    orbit.wheelPrecision = 0.5;
    // Smooth camera-pan + scrolling
    orbit.panningSensibility = 1000;
    orbit.useAutoRotationBehavior = false;
    orbit.inertia = 0.85;
  }

  return scene;
}


// ────────────────────────────────────────────────────────────────────
//  View-Cube — snap the ArcRotateCamera to canonical views.
//  Animates `alpha` / `beta` to standard architect-view angles over
//  ~400 ms for a smooth feel. Only meaningful in Orbit mode (walk
//  camera ignores these targets).
// ────────────────────────────────────────────────────────────────────
const VIEW_PRESETS = {
  // alpha, beta (radians).  Babylon: alpha rotates around Y, beta is
  // the polar angle from +Y. Beta close to 0 → camera looking straight
  // down (top view); beta close to π → straight up (bottom).
  top:    { alpha: -Math.PI / 2, beta: 0.001, label: "Top" },
  bottom: { alpha: -Math.PI / 2, beta: Math.PI - 0.001, label: "Bottom" },
  front:  { alpha: -Math.PI / 2, beta: Math.PI / 2, label: "Front (N)" },
  back:   { alpha:  Math.PI / 2, beta: Math.PI / 2, label: "Back (S)" },
  left:   { alpha:  Math.PI,     beta: Math.PI / 2, label: "Left (W)" },
  right:  { alpha:  0,           beta: Math.PI / 2, label: "Right (E)" },
  iso:    { alpha: -Math.PI / 4, beta: Math.PI / 3, label: "Isometric" },
};


// ────────────────────────────────────────────────────────────────────
// Fit-all — re-frame the orbit camera so every equipment mesh is in
// view. Walks scene.meshes, skips the ground / skybox / grid helpers,
// builds a combined bounding box and snaps the ArcRotateCamera target
// + radius accordingly. Works the same across all process-plant
// sub-industries and country profiles because it relies purely on
// mesh geometry — no domain assumptions.
// ────────────────────────────────────────────────────────────────────
function fitAllInScene(scene) {
  if (!scene) return false;
  const cam = scene.getCameraByName("orbit");
  if (!cam || cam.getClassName() !== "ArcRotateCamera") return false;

  const SKIP = /^(ground|sky|skybox|grid|north|compass|tile|terrain)/i;
  let min = null, max = null;
  for (const m of scene.meshes) {
    if (!m || !m.isEnabled() || !m.getTotalVertices()) continue;
    if (SKIP.test(m.name || "")) continue;
    try { m.refreshBoundingInfo(); } catch { /* ignored */ }
    const bi = m.getBoundingInfo();
    if (!bi) continue;
    const mn = bi.boundingBox.minimumWorld;
    const mx = bi.boundingBox.maximumWorld;
    min = min ? new BABYLON.Vector3(Math.min(min.x, mn.x),
                                    Math.min(min.y, mn.y),
                                    Math.min(min.z, mn.z)) : mn.clone();
    max = max ? new BABYLON.Vector3(Math.max(max.x, mx.x),
                                    Math.max(max.y, mx.y),
                                    Math.max(max.z, mx.z)) : mx.clone();
  }
  if (!min || !max) return false;
  const centre = min.add(max).scale(0.5);
  const extent = max.subtract(min);
  const radius = Math.max(extent.x, extent.y, extent.z) * 1.4 || 200;

  // Animate to feel responsive.
  const dur = 30;
  BABYLON.Animation.CreateAndStartAnimation(
      "fit-target", cam, "target", 60, dur, cam.target, centre,
      BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT);
  BABYLON.Animation.CreateAndStartAnimation(
      "fit-radius", cam, "radius", 60, dur, cam.radius, radius,
      BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT);
  // Reset the limits so the user can keep zooming after the snap.
  cam.lowerRadiusLimit = Math.max(5, radius * 0.05);
  cam.upperRadiusLimit = radius * 8;
  return true;
}


function snapOrbitCamera(scene, preset) {
  const cam = scene && scene.getCameraByName("orbit");
  if (!cam || cam.getClassName() !== "ArcRotateCamera") return false;
  const target = VIEW_PRESETS[preset];
  if (!target) return false;
  const dur = 30;   // ~0.5s @ 60 fps
  BABYLON.Animation.CreateAndStartAnimation(
    `vc_alpha_${preset}`, cam, "alpha", 60, dur,
    cam.alpha, target.alpha,
    BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT);
  BABYLON.Animation.CreateAndStartAnimation(
    `vc_beta_${preset}`, cam, "beta", 60, dur,
    cam.beta, target.beta,
    BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT);
  return true;
}


function ViewCubeCell({ id, hidden, getScene, testIdPrefix }) {
  if (hidden) return <div style={{ width: 32, height: 28 }} />;
  const v = VIEW_PRESETS[id];
  if (!v) return null;
  return (
    <button onClick={() => snapOrbitCamera(getScene(), id)}
            data-testid={`${testIdPrefix}-${id}`}
            title={v.label}
            style={{ width: 32, height: 28, padding: 0,
                      fontSize: 9, lineHeight: "12px",
                      background: "rgba(15, 19, 28, 0.85)",
                      color: "var(--pf-text)",
                      border: "1px solid var(--pf-border)",
                      borderRadius: 3, cursor: "pointer",
                      fontFamily: "'JetBrains Mono', monospace",
                      textAlign: "center" }}>
      {v.label.split(" ")[0].slice(0, 5)}
    </button>
  );
}

function ViewCube({ getScene, testIdPrefix = "viewcube" }) {
  // 7 standard snap views laid out as a compact 3 × 3 grid.
  // Cell map: TL=Iso, TM=Top, TR=Back; ML=Left, MM=•, MR=Right;
  //          BL=Front, BM=Bottom, BR=•
  const C = (props) => (
    <ViewCubeCell {...props} getScene={getScene}
                    testIdPrefix={testIdPrefix}/>
  );
  return (
    <div data-testid={testIdPrefix}
         style={{ position: "absolute", top: 10, right: 10,
                   display: "grid",
                   gridTemplateColumns: "32px 32px 32px",
                   gridGap: 3, zIndex: 5 }}>
      <C id="iso"/>     <C id="top"/>    <C id="back"/>
      <C id="left"/>    <div/>            <C id="right"/>
      <C id="front"/>   <C id="bottom"/>  <div/>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────
//  Per-type 3D mesh factory — mirrors the SVG symbol set so each
//  equipment item reads as the real thing instead of a generic
//  primitive (column = tall cylinder, sphere_tank = sphere,
//  heater = box + stack, building = box + pitched roof, etc.).
// ────────────────────────────────────────────────────────────────────
function _buildEquipMesh(eq, scene) {
  const et = eq.equip_type || "generic";
  const w  = eq.width_m || (eq.radius_m || 1) * 2;
  const dep = eq.depth_m || (eq.radius_m || 1) * 2;
  const h  = eq.height_m || 6;
  const color = BABYLON.Color3.FromHexString(eq.color || "#4b5563");
  const mat = (name, c = color, alpha = 1) => {
    const m = new BABYLON.StandardMaterial(name, scene);
    m.diffuseColor = c;
    m.specularColor = new BABYLON.Color3(0.18, 0.18, 0.22);
    if (alpha !== 1) m.alpha = alpha;
    return m;
  };
  const root = new BABYLON.TransformNode(`root_${eq.tag}`, scene);

  const addChild = (mesh) => {
    mesh.parent = root;
    mesh.isPickable = true;
    return mesh;
  };

  if (et === "column" || et === "tower" || et === "silo") {
    // Tall slender vertical cylinder + spherical top head
    const r = (eq.radius_m || w / 2);
    const cyl = BABYLON.MeshBuilder.CreateCylinder(eq.tag, {
      diameter: r * 2, height: h * 0.92, tessellation: 28,
    }, scene);
    cyl.position.y = h * 0.46;
    cyl.material = mat(`m_${eq.tag}`);
    addChild(cyl);
    // Hemispherical top head
    const head = BABYLON.MeshBuilder.CreateSphere(`${eq.tag}_top`, {
      diameter: r * 2 * 1.02, slice: 0.5, segments: 18,
    }, scene);
    head.position.y = h * 0.92;
    head.material = mat(`m_${eq.tag}_top`);
    addChild(head);
    // Skirt (foundation)
    const sk = BABYLON.MeshBuilder.CreateCylinder(`${eq.tag}_skirt`, {
      diameter: r * 2 * 1.15, height: 1.5, tessellation: 18,
    }, scene);
    sk.position.y = 0.75;
    sk.material = mat(`m_${eq.tag}_sk`,
      BABYLON.Color3.FromHexString("#3a3f4a"));
    addChild(sk);
  }
  else if (et === "reactor") {
    const r = (eq.radius_m || w / 2);
    const cyl = BABYLON.MeshBuilder.CreateCylinder(eq.tag, {
      diameter: r * 2, height: h * 0.85, tessellation: 24,
    }, scene);
    cyl.position.y = h * 0.425;
    cyl.material = mat(`m_${eq.tag}`);
    addChild(cyl);
    // Hemisphere top + bottom for high-pressure look
    const top = BABYLON.MeshBuilder.CreateSphere(`${eq.tag}_t`, {
      diameter: r * 2, slice: 0.5, segments: 18,
    }, scene);
    top.position.y = h * 0.85;
    top.material = mat(`m_${eq.tag}_t`); addChild(top);
    const bot = BABYLON.MeshBuilder.CreateSphere(`${eq.tag}_b`, {
      diameter: r * 2, slice: 0.5, segments: 18,
    }, scene);
    bot.rotation.x = Math.PI;
    bot.position.y = 0;
    bot.material = mat(`m_${eq.tag}_b`); addChild(bot);
  }
  else if (et === "drum") {
    // Horizontal cylinder (axis along Z) on saddles
    const D = Math.min(w, dep);
    const L = Math.max(w, dep);
    const cyl = BABYLON.MeshBuilder.CreateCylinder(eq.tag, {
      diameter: D, height: L, tessellation: 24,
    }, scene);
    cyl.rotation.x = Math.PI / 2;
    cyl.position.y = D / 2 + 0.8;
    cyl.material = mat(`m_${eq.tag}`);
    addChild(cyl);
    // Saddles
    [-L * 0.3, L * 0.3].forEach((zo, i) => {
      const s = BABYLON.MeshBuilder.CreateBox(`${eq.tag}_sd${i}`, {
        width: D * 1.05, height: 0.8, depth: D * 0.25,
      }, scene);
      s.position.set(0, 0.4, zo);
      s.material = mat(`m_${eq.tag}_sd${i}`,
        BABYLON.Color3.FromHexString("#3a3f4a"));
      addChild(s);
    });
  }
  else if (et === "heater") {
    // Firebox + tall stack
    const box = BABYLON.MeshBuilder.CreateBox(eq.tag, {
      width: w, depth: dep, height: h * 0.55,
    }, scene);
    box.position.y = h * 0.275;
    box.material = mat(`m_${eq.tag}`);
    addChild(box);
    const stk = BABYLON.MeshBuilder.CreateCylinder(`${eq.tag}_stk`, {
      diameter: Math.min(w, dep) * 0.18,
      height: h * 0.7, tessellation: 16,
    }, scene);
    stk.position.set(w * 0.32, h * 0.55 + h * 0.35, dep * 0.32);
    stk.material = mat(`m_${eq.tag}_stk`,
      BABYLON.Color3.FromHexString("#1f1f1f"));
    addChild(stk);
  }
  else if (et === "exchanger") {
    // Long horizontal cylinder bank
    const L = Math.max(w, dep);
    const D = Math.min(w, dep) * 0.55;
    const bays = Math.max(1, Math.round(Math.min(w, dep) / (D + 1)));
    for (let i = 0; i < bays; i++) {
      const cy = BABYLON.MeshBuilder.CreateCylinder(`${eq.tag}_b${i}`, {
        diameter: D, height: L, tessellation: 18,
      }, scene);
      cy.rotation.x = Math.PI / 2;
      const off = (i - (bays - 1) / 2) * (D + 0.5);
      cy.position.set(off * Math.sin(0), D / 2 + 0.8, 0);
      // Lay bays orthogonal to L
      if (w >= dep) cy.position.x = off;
      else { cy.rotation.x = 0; cy.rotation.z = Math.PI / 2; cy.position.z = off; }
      cy.material = mat(`m_${eq.tag}_b${i}`);
      addChild(cy);
    }
  }
  else if (et === "tank") {
    const r = (eq.radius_m || w / 2);
    const cyl = BABYLON.MeshBuilder.CreateCylinder(eq.tag, {
      diameter: r * 2, height: h, tessellation: 32,
    }, scene);
    cyl.position.y = h / 2;
    cyl.material = mat(`m_${eq.tag}`);
    addChild(cyl);
    // Cone / dome roof
    const roof = BABYLON.MeshBuilder.CreateCylinder(`${eq.tag}_rf`, {
      diameterTop: 0, diameterBottom: r * 2, height: r * 0.12,
      tessellation: 32,
    }, scene);
    roof.position.y = h + r * 0.06;
    roof.material = mat(`m_${eq.tag}_rf`,
      BABYLON.Color3.FromHexString("#5a5f6a"));
    addChild(roof);
  }
  else if (et === "sphere_tank") {
    const D = Math.min(w, dep);
    const s = BABYLON.MeshBuilder.CreateSphere(eq.tag, {
      diameter: D, segments: 22,
    }, scene);
    s.position.y = D * 0.6;
    s.material = mat(`m_${eq.tag}`);
    addChild(s);
    // 4 legs
    for (let k = 0; k < 4; k++) {
      const ang = (k / 4) * Math.PI * 2;
      const leg = BABYLON.MeshBuilder.CreateCylinder(`${eq.tag}_lg${k}`, {
        diameter: 0.6, height: D * 0.55, tessellation: 8,
      }, scene);
      leg.position.set(Math.cos(ang) * D * 0.35,
        D * 0.275, Math.sin(ang) * D * 0.35);
      leg.material = mat(`m_${eq.tag}_lg${k}`,
        BABYLON.Color3.FromHexString("#3a3f4a"));
      addChild(leg);
    }
  }
  else if (et === "bullet") {
    // Mounded LPG bullet — capsule (cylinder + 2 hemispheres) buried
    // up to ~half-height in an earthen mound (slight cone berm).
    const L = Math.max(w, dep);
    const D = Math.min(w, dep);
    const cyl = BABYLON.MeshBuilder.CreateCylinder(eq.tag, {
      diameter: D, height: L * 0.8, tessellation: 22,
    }, scene);
    cyl.rotation.x = Math.PI / 2;
    cyl.position.y = D * 0.5;
    cyl.material = mat(`m_${eq.tag}`);
    addChild(cyl);
    // Earth mound
    const mound = BABYLON.MeshBuilder.CreateCylinder(`${eq.tag}_md`, {
      diameterTop: D * 0.4, diameterBottom: D * 1.6,
      height: D * 0.5, tessellation: 22,
    }, scene);
    mound.position.y = D * 0.25;
    mound.material = mat(`m_${eq.tag}_md`,
      BABYLON.Color3.FromHexString("#7c5a2e"));
    addChild(mound);
  }
  else if (et === "flare") {
    // Very tall thin pylon + flame cone at the top
    const r = (eq.radius_m || w / 2);
    const stack = BABYLON.MeshBuilder.CreateCylinder(eq.tag, {
      diameter: r * 2, height: h, tessellation: 16,
    }, scene);
    stack.position.y = h / 2;
    stack.material = mat(`m_${eq.tag}`,
      BABYLON.Color3.FromHexString("#3a3f4a"));
    addChild(stack);
    // Flame (orange tip)
    const flame = BABYLON.MeshBuilder.CreateCylinder(`${eq.tag}_fl`, {
      diameterTop: 0, diameterBottom: r * 3.5,
      height: r * 5, tessellation: 18,
    }, scene);
    flame.position.y = h + r * 2.5;
    const flMat = mat(`m_${eq.tag}_fl`,
      BABYLON.Color3.FromHexString("#f26b1f"), 0.85);
    flMat.emissiveColor = BABYLON.Color3.FromHexString("#f26b1f");
    flame.material = flMat;
    addChild(flame);
  }
  else if (et === "pump") {
    // Pump impeller (small cylinder) + driver block
    const D = Math.min(w, dep) * 0.7;
    const imp = BABYLON.MeshBuilder.CreateCylinder(eq.tag, {
      diameter: D, height: D, tessellation: 18,
    }, scene);
    imp.position.set(-w * 0.18, D / 2 + 0.3, 0);
    imp.material = mat(`m_${eq.tag}`);
    addChild(imp);
    const drv = BABYLON.MeshBuilder.CreateBox(`${eq.tag}_d`, {
      width: w * 0.45, depth: dep * 0.6, height: D * 1.0,
    }, scene);
    drv.position.set(w * 0.18, D / 2 + 0.3, 0);
    drv.material = mat(`m_${eq.tag}_d`,
      BABYLON.Color3.FromHexString("#1f1f1f"));
    addChild(drv);
  }
  else if (et === "compressor") {
    const main = BABYLON.MeshBuilder.CreateBox(eq.tag, {
      width: w * 0.8, depth: dep, height: h * 0.85,
    }, scene);
    main.position.set(-w * 0.1, h * 0.425, 0);
    main.material = mat(`m_${eq.tag}`);
    addChild(main);
    const driver = BABYLON.MeshBuilder.CreateBox(`${eq.tag}_dr`, {
      width: w * 0.3, depth: dep * 0.65, height: h * 0.6,
    }, scene);
    driver.position.set(w * 0.32, h * 0.3, 0);
    driver.material = mat(`m_${eq.tag}_dr`,
      BABYLON.Color3.FromHexString("#1f1f1f"));
    addChild(driver);
  }
  else if (et === "cooling_tower") {
    // Multi-cell box with louver lines
    const cells = Math.max(2, Math.round(w / (dep * 1.1)));
    for (let i = 0; i < cells; i++) {
      const cw = w / cells;
      const cell = BABYLON.MeshBuilder.CreateBox(`${eq.tag}_c${i}`, {
        width: cw * 0.92, depth: dep, height: h,
      }, scene);
      cell.position.set(-w / 2 + cw * (i + 0.5), h / 2, 0);
      cell.material = mat(`m_${eq.tag}_c${i}`);
      addChild(cell);
      // Fan ring on top
      const fan = BABYLON.MeshBuilder.CreateCylinder(`${eq.tag}_fan${i}`, {
        diameter: Math.min(cw, dep) * 0.7, height: 0.4, tessellation: 16,
      }, scene);
      fan.position.set(-w / 2 + cw * (i + 0.5), h + 0.2, 0);
      fan.material = mat(`m_${eq.tag}_fan${i}`,
        BABYLON.Color3.FromHexString("#1f1f1f"));
      addChild(fan);
    }
  }
  else if (et === "boiler") {
    const body = BABYLON.MeshBuilder.CreateBox(eq.tag, {
      width: w, depth: dep, height: h * 0.7,
    }, scene);
    body.position.y = h * 0.35;
    body.material = mat(`m_${eq.tag}`);
    addChild(body);
    const stk = BABYLON.MeshBuilder.CreateCylinder(`${eq.tag}_stk`, {
      diameter: Math.min(w, dep) * 0.16,
      height: h * 0.5, tessellation: 14,
    }, scene);
    stk.position.set(0, h * 0.7 + h * 0.25, 0);
    stk.material = mat(`m_${eq.tag}_stk`,
      BABYLON.Color3.FromHexString("#1f1f1f"));
    addChild(stk);
  }
  else if (et === "etp" || et === "wtp") {
    // Pond / basin — thin slab with water surface
    const slab = BABYLON.MeshBuilder.CreateBox(eq.tag, {
      width: w, depth: dep, height: 2,
    }, scene);
    slab.position.y = 1;
    slab.material = mat(`m_${eq.tag}`,
      BABYLON.Color3.FromHexString("#3a3f4a"));
    addChild(slab);
    const water = BABYLON.MeshBuilder.CreateGround(`${eq.tag}_w`, {
      width: w * 0.94, height: dep * 0.94,
    }, scene);
    water.position.y = 2.05;
    const wmat = mat(`m_${eq.tag}_w`,
      BABYLON.Color3.FromHexString("#0e7c6b"), 0.7);
    water.material = wmat;
    addChild(water);
  }
  else if (et === "substation") {
    // Yard plate + 3 transformer boxes + bus-bar towers
    const plate = BABYLON.MeshBuilder.CreateBox(eq.tag, {
      width: w, depth: dep, height: 0.5,
    }, scene);
    plate.position.y = 0.25;
    plate.material = mat(`m_${eq.tag}`,
      BABYLON.Color3.FromHexString("#3a3f4a"));
    addChild(plate);
    for (let k = 0; k < 3; k++) {
      const txm = BABYLON.MeshBuilder.CreateBox(`${eq.tag}_tx${k}`, {
        width: w * 0.18, depth: dep * 0.3, height: h * 0.55,
      }, scene);
      txm.position.set(-w * 0.3 + k * w * 0.3, h * 0.55 / 2 + 0.5, 0);
      txm.material = mat(`m_${eq.tag}_tx${k}`,
        BABYLON.Color3.FromHexString("#1e4e8c"));
      addChild(txm);
    }
    // Lattice tower on one corner
    const tower = BABYLON.MeshBuilder.CreateCylinder(`${eq.tag}_tw`, {
      diameter: 0.6, height: h, tessellation: 6,
    }, scene);
    tower.position.set(-w * 0.4, h / 2, dep * 0.35);
    tower.material = mat(`m_${eq.tag}_tw`,
      BABYLON.Color3.FromHexString("#9aa2b1"));
    addChild(tower);
  }
  else if (et === "parking") {
    // Just a thin slab — no vertical extent
    const slab = BABYLON.MeshBuilder.CreateBox(eq.tag, {
      width: w, depth: dep, height: 0.2,
    }, scene);
    slab.position.y = 0.1;
    slab.material = mat(`m_${eq.tag}`,
      BABYLON.Color3.FromHexString("#9aa2b1"));
    addChild(slab);
  }
  else if (et === "control_room" || et === "admin_bldg" || et === "lab"
           || et === "fire_house" || et === "workshop"
           || et === "canteen" || et === "gate_house") {
    // Building: box + pitched roof + door
    const body = BABYLON.MeshBuilder.CreateBox(eq.tag, {
      width: w, depth: dep, height: h * 0.75,
    }, scene);
    body.position.y = h * 0.375;
    body.material = mat(`m_${eq.tag}`);
    addChild(body);
    // Pitched roof — prism
    const roof = BABYLON.MeshBuilder.CreateCylinder(`${eq.tag}_rf`, {
      diameterTop: 0.01, diameterBottom: 0.01,
      height: 0.01, tessellation: 4,
    }, scene);
    roof.dispose();
    const roofMesh = BABYLON.MeshBuilder.CreateBox(`${eq.tag}_rfbox`, {
      width: w * 0.98, depth: dep * 0.98, height: h * 0.18,
    }, scene);
    roofMesh.position.y = h * 0.84;
    roofMesh.scaling.y = 0.6;
    const rfMat = mat(`m_${eq.tag}_rf`,
      BABYLON.Color3.FromHexString("#7a2e1f"));
    roofMesh.material = rfMat;
    addChild(roofMesh);
    // Door
    const door = BABYLON.MeshBuilder.CreateBox(`${eq.tag}_dr`, {
      width: Math.min(2.2, w * 0.18),
      depth: 0.2,
      height: Math.min(2.8, h * 0.4),
    }, scene);
    door.position.set(0, Math.min(1.4, h * 0.2),
      -dep / 2 + 0.11);
    door.material = mat(`m_${eq.tag}_dr`,
      BABYLON.Color3.FromHexString("#1f1f1f"));
    addChild(door);
  }
  else {
    // Generic fallback
    if (eq.primitive === "cylinder") {
      const cyl = BABYLON.MeshBuilder.CreateCylinder(eq.tag, {
        diameter: (eq.radius_m || w / 2) * 2,
        height: h, tessellation: 18,
      }, scene);
      cyl.position.y = h / 2;
      cyl.material = mat(`m_${eq.tag}`);
      addChild(cyl);
    } else {
      const box = BABYLON.MeshBuilder.CreateBox(eq.tag, {
        width: w, depth: dep, height: h,
      }, scene);
      box.position.y = h / 2;
      box.material = mat(`m_${eq.tag}`);
      addChild(box);
    }
  }
  return root;
}



// ────────────────────────────────────────────────────────────────────
//  Hover tooltip — driven by Babylon pointer-pick events.
//  Reads metadata {tag, name, equip_type, zone} from the picked mesh
//  (set in build3DScene) and surfaces it via React state so the
//  overlay div can position next to the mouse.
// ────────────────────────────────────────────────────────────────────
function _attachHoverTooltip(scene, canvas, setHover) {
  let lastTag = null;
  let lastTs = 0;
  scene.onPointerObservable.add((pi) => {
    if (pi.type !== BABYLON.PointerEventTypes.POINTERMOVE) return;
    // Throttle to ~60 fps and pick whatever the pointer is currently over.
    const now = performance.now();
    if (now - lastTs < 16) return;
    lastTs = now;
    const info = scene.pick(scene.pointerX, scene.pointerY,
      (m) => !!(m && m.isPickable && m.metadata && m.metadata.tag));
    const evt = pi.event;
    if (!info || !info.hit || !info.pickedMesh || !info.pickedMesh.metadata) {
      if (lastTag !== null) { setHover(null); lastTag = null; }
      return;
    }
    const md = info.pickedMesh.metadata;
    const label = `${md.tag} — ${md.name}`
      + (md.equip_type ? `  (${md.equip_type})` : "")
      + (md.zone ? ` · zone: ${md.zone}` : "");
    const rect = canvas.getBoundingClientRect();
    if (md.tag !== lastTag) {
      lastTag = md.tag;
      setHover({
        x: evt.clientX - rect.left,
        y: evt.clientY - rect.top,
        label,
      });
    } else {
      setHover((h) => h ? { ...h,
        x: evt.clientX - rect.left,
        y: evt.clientY - rect.top } : h);
    }
  });
}
